import { computed } from 'vue'
import type { Product, ProductCategory } from '@scayle/storefront-nuxt'
import type { SbDynamicBadge, SbBadgeText } from '~/cms/storyblok/types/storyblok.gen'

// Matches Storyblok's native-color-picker field type
interface StoryblokColorPicker {
  _uid: string
  color: string
  plugin: 'native-color-picker'
}

interface ExtendedSbBadgeText extends SbBadgeText {
  internalName?: string
  label?: string
  colorText?: StoryblokColorPicker
  colorBackground?: StoryblokColorPicker
  _uid: string
  component: 'BadgeText'
}

export interface DynamicBadgeDisplay {
  label: string
  backgroundColor: string
  textColor: string
  replaceSale: boolean
}

/**
 * Check if a product belongs to a specific category
 * Searches through all category paths of the product
 */
const isInCategory = (product: Product, categoryId: string): boolean => {
  const categories = product.categories || []
  return categories.some(categoryPath =>
    categoryPath.some(category => String(category.categoryId) === categoryId)
  )
}

/**
 * Validate if a badge should be displayed for a product
 * Checks active status, date range, and category conditions
 */
const isValidBadge = (badge: SbDynamicBadge, product: Product): boolean => {
  const now = new Date()
  const from = badge.from ? new Date(badge.from + 'Z') : new Date(0)
  const till = badge.till ? new Date(badge.till + 'Z') : new Date(8640000000000000)
  const isInDateRange = now >= from && now < till
  const matchesCategory = !badge.conditionCategoryID || isInCategory(product, badge.conditionCategoryID)

  return Boolean(badge.active && isInDateRange && matchesCategory)
}

export const useDynamicBadges = (product: Product, badges: SbDynamicBadge[]) => {
  const activeBadges = computed(() => {
    return badges
      .filter(badge => isValidBadge(badge, product))
      .map(badge => {
        const badgeText = badge.badge?.[0] as ExtendedSbBadgeText
        if (!badgeText) return null

        return {
          label: badgeText.label || '',
          backgroundColor: badgeText.colorBackground?.color || 'transparent',
          textColor: badgeText.colorText?.color || 'currentColor',
          replaceSale: badge.replaceSale || false
        } satisfies DynamicBadgeDisplay
      })
      .filter((badge): badge is DynamicBadgeDisplay => badge !== null)
  })

  return {
    activeBadges
  }
} 